<script setup lang="ts">
import { computed, ref } from 'vue';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { useUtilities } from '@apparatix/composables';
import { Noir } from '@apparatix/theme/Noir';
import { Toolbar, Typography } from '@apparatix/library/core';
import type { RelationshipType } from '@apparatix/schemas/Relationship';
import { SectionIconography } from './SectionIconography';
import BillboardIcon from '@apparatix/icons/BillboardIcon.vue';
import BlockableMenuItem from './BlockableMenuItem.vue';

type Props = {
  type: RelationshipType,
  title: string,
  subtitle?: string,
  menuItems?: MenuItem[],
  /**
   * By default, the hamburger menu is only shown on mobile.
   * Setting this prop to true will make it appear on desktop
   */
  forceMenuButtonOnDesktop?: boolean,
};

const props = defineProps<Props>();

const { arrayHasValues } = useUtilities();

const menu = ref();
const menuId = ref(crypto.randomUUID());

const isInventory = computed(() => props.type === 'Inventory');

const headerConfig = computed(() => SectionIconography[props.type]);

const avatarStyle = computed(() => ({
  backgroundColor: headerConfig.value.backgroundColor,
  color: headerConfig.value.color,
}));

// CRM types use a PrimeIcon class; inventory renders a custom SVG via the Avatar slot.
const avatarIcon = computed(() => isInventory.value ? undefined : headerConfig.value.icon);

const onMenuButtonclick = (e: MouseEvent) => menu.value.toggle(e);
</script>
<template>
  <div class="flex justify-between p-4">
    <div class="flex items-center gap-3">
      <div class="hidden lg:block">
        <Avatar
          shape="circle"
          size="xlarge"
          :icon="avatarIcon"
          :style="avatarStyle"
        >
          <BillboardIcon
            v-if="isInventory"
            :width="26"
            :height="26"
            :color="headerConfig.color"
          />
        </Avatar>
      </div>
      <div class="lg:hidden">
        <Avatar
          shape="circle"
          size="large"
          :icon="avatarIcon"
          :style="avatarStyle"
        >
          <BillboardIcon
            v-if="isInventory"
            :width="22"
            :height="22"
            :color="headerConfig.color"
          />
        </Avatar>
      </div>
      <div>
        <Typography variant="drawer-title">
          {{ props.title }}
        </Typography>
        <div class="flex items-center gap-2">
          <Typography variant="drawer-subtitle">
            {{ props.subtitle }}
          </Typography>
          <slot name="subtitle-items" />
        </div>
      </div>
    </div>
    <Toolbar class="hidden lg:flex">
      <slot name="buttons" />
      <Button
        v-if="props.forceMenuButtonOnDesktop"
        icon="pi pi-bars"
        variant="text"
        aria-label="Open menu"
        aria-haspopup="true"
        :aria-controls="menuId"
        :dt="Noir.button"
        rounded
        @click="onMenuButtonclick"
      />
    </Toolbar>
    <Toolbar v-if="arrayHasValues(props.menuItems)" class="flex lg:hidden">
      <Button
        icon="pi pi-bars"
        variant="text"
        aria-label="Open menu"
        aria-haspopup="true"
        :aria-controls="menuId"
        :dt="Noir.button"
        rounded
        @click="onMenuButtonclick"
      />
    </Toolbar>
  </div>
  <Menu
    :id="menuId"
    ref="menu"
    :model="props.menuItems"
    popup
  >
    <template #item="{ item, props: itemProps }">
      <BlockableMenuItem :item="item" :action-props="itemProps.action" />
    </template>
  </Menu>
</template>
